/* ╔══════════════════════════════════════════════════════════════════╗
   ║  La Boutique Saint-Pierre — comportements globaux                   ║
   ║  En-tête (ombre au défilement), menu mobile, tiroir panier          ║
   ║  (Section Rendering API), quantités, langues, apparitions.          ║
   ╚══════════════════════════════════════════════════════════════════╝ */
(() => {
  const BSP = (window.BSP = window.BSP || {});
  const reduitMouvement = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ─── Piège à focus pour les tiroirs ─────────────────────────────── */
  const focusables = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])';
  function ouvrirTiroir(tiroir, declencheur) {
    if (!tiroir) return;
    tiroir._declencheur = declencheur || document.activeElement;
    tiroir.hidden = false;
    // Pas de requestAnimationFrame : il ne se déclenche pas dans un onglet en arrière-plan.
    void tiroir.offsetWidth;
    tiroir.classList.add('is-ouvert');
    tiroir.setAttribute('aria-hidden', 'false');
    document.body.classList.add('bsp-verrou');
    const cible = tiroir.querySelector('[data-focus-initial]') || tiroir.querySelector(focusables);
    cible && setTimeout(() => cible.focus({ preventScroll: true }), 60);
    tiroir._clavier = (e) => {
      if (e.key === 'Escape') fermerTiroir(tiroir);
      if (e.key !== 'Tab') return;
      const els = Array.from(tiroir.querySelectorAll(focusables)).filter((el) => el.offsetParent !== null);
      if (!els.length) return;
      const premier = els[0];
      const dernier = els[els.length - 1];
      if (e.shiftKey && document.activeElement === premier) { e.preventDefault(); dernier.focus(); }
      else if (!e.shiftKey && document.activeElement === dernier) { e.preventDefault(); premier.focus(); }
    };
    document.addEventListener('keydown', tiroir._clavier);
  }
  function fermerTiroir(tiroir) {
    if (!tiroir || !tiroir.classList.contains('is-ouvert')) return;
    tiroir.classList.remove('is-ouvert');
    tiroir.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('bsp-verrou');
    document.removeEventListener('keydown', tiroir._clavier);
    tiroir._declencheur && tiroir._declencheur.focus && tiroir._declencheur.focus({ preventScroll: true });
  }
  BSP.ouvrirTiroir = ouvrirTiroir;
  BSP.fermerTiroir = fermerTiroir;

  /* ─── En-tête ────────────────────────────────────────────────────── */
  function initEntete() {
    const entete = document.querySelector('[data-bsp-entete]');
    if (!entete) return;
    const groupe = entete.closest('.shopify-section');
    const racine = document.documentElement;
    let hauteur = entete.getBoundingClientRect().height;
    let dernier = Math.max(0, window.scrollY);
    let cache = false;
    // Fond blanc dès que l'en-tête a quitté sa place ; caché en descendant, de retour en remontant.
    // --bsp-entete-visible = hauteur de l'en-tête quand il est collé et visible (sinon 0) : les
    // éléments collants des pages (aperçu, récapitulatif) se décalent d'autant.
    const maj = () => {
      const y = Math.max(0, window.scrollY);
      // Place de l'en-tête dans la page = bas du bandeau d'annonce (offsetTop d'un élément collé suit le défilement)
      const avant = groupe && groupe.previousElementSibling;
      const debut = avant && avant.classList.contains('shopify-section') ? avant.getBoundingClientRect().bottom + y : 0;
      const colle = y > debut + 1;
      const delta = y - dernier;
      if (y <= debut + hauteur) { cache = false; dernier = y; }
      else if (Math.abs(delta) > 6) {
        const tiroirOuvert = document.querySelector('.bsp-tiroir-menu.is-ouvert, .bsp-tiroir.is-ouvert');
        cache = delta > 0 && !tiroirOuvert && !entete.contains(document.activeElement);
        dernier = y;
      }
      entete.classList.toggle('is-defile', colle);
      entete.classList.toggle('is-cache', cache);
      racine.style.setProperty('--bsp-entete-visible', colle && !cache ? `${hauteur}px` : '0px');
    };
    maj();
    window.addEventListener('scroll', maj, { passive: true });
    // Le clavier fait réapparaître l'en-tête (tabulation vers le menu)
    entete.addEventListener('focusin', () => { cache = false; maj(); });
    const mesurer = () => {
      hauteur = entete.getBoundingClientRect().height;
      racine.style.setProperty('--bsp-entete-reel', `${hauteur}px`);
      maj();
    };
    mesurer();
    'ResizeObserver' in window && new ResizeObserver(mesurer).observe(entete);
  }

  /* ─── Panier ─────────────────────────────────────────────────────── */
  function tiroirPanier() { return document.querySelector('[data-bsp-tiroir-panier]'); }

  BSP.majPanier = function majPanier(sections, { ouvrir = false } = {}) {
    if (sections && sections['bsp-panier-tiroir']) {
      const tmp = document.createElement('div');
      tmp.innerHTML = sections['bsp-panier-tiroir'];
      const neuf = tmp.querySelector('[data-bsp-tiroir-contenu]');
      const actuel = document.querySelector('[data-bsp-tiroir-contenu]');
      if (neuf && actuel) {
        actuel.replaceWith(neuf);
        document.dispatchEvent(new CustomEvent('bsp:panier:rendu', { detail: { racine: neuf } }));
      }
      const compte = neuf ? parseInt(neuf.dataset.compte || '0', 10) : null;
      if (compte !== null) majPastilles(compte);
    }
    if (ouvrir) {
      if (BSP.panierType === 'page') location.href = BSP.routes.panier;
      else ouvrirTiroir(tiroirPanier());
    }
  };

  function majPastilles(compte) {
    document.querySelectorAll('[data-bsp-compte-panier-texte]').forEach((t) => {
      t.textContent = compte;
      t.classList.remove('is-pulse'); void t.offsetWidth; t.classList.add('bsp-pastille-texte', 'is-pulse');
    });
    document.querySelectorAll('[data-bsp-compte-panier]').forEach((p) => {
      p.textContent = compte;
      p.hidden = compte < 1;
      p.classList.remove('is-pulse');
      void p.offsetWidth;
      p.classList.add('is-pulse');
    });
  }

  async function changerLigne(cle, quantite, ligne) {
    ligne && ligne.classList.add('is-chargement');
    const surPage = !!document.querySelector('[data-bsp-page-panier]');
    try {
      const r = await fetch(`${BSP.routes.changer}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: cle, quantity: quantite, sections: 'bsp-panier-tiroir', sections_url: location.pathname }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.description || data.message);
      if (surPage) { location.reload(); return; }
      BSP.majPanier(data.sections);
    } catch (e) {
      ligne && ligne.classList.remove('is-chargement');
      const err = document.querySelector('[data-bsp-erreur-panier]');
      if (err) { err.textContent = e.message || (BSP.textes && BSP.textes.erreurPanier); err.hidden = false; }
    }
  }

  // Plusieurs lignes d'un coup (un lot de fourchettes réparti sur deux prix).
  async function retirerLignes(cles, ligne) {
    ligne && ligne.classList.add('is-chargement');
    const updates = {};
    cles.forEach((k) => { updates[k] = 0; });
    try {
      const r = await fetch(`${BSP.routes.maj}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ updates, sections: 'bsp-panier-tiroir', sections_url: location.pathname }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.description || data.message);
      if (document.querySelector('[data-bsp-page-panier]')) { location.reload(); return; }
      BSP.majPanier(data.sections);
    } catch (e) {
      ligne && ligne.classList.remove('is-chargement');
      const err = document.querySelector('[data-bsp-erreur-panier]');
      if (err) { err.textContent = e.message || (BSP.textes && BSP.textes.erreurPanier); err.hidden = false; }
    }
  }

  /* ─── Code promo ─────────────────────────────────────────────────── */
  async function appliquerCodes(codes, zone) {
    const erreur = zone && zone.querySelector('[data-bsp-code-erreur]');
    if (erreur) erreur.hidden = true;
    zone && zone.classList.add('is-chargement');
    try {
      const r = await fetch(`${BSP.routes.maj}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ discount: codes.join(','), sections: 'bsp-panier-tiroir', sections_url: location.pathname }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.description || data.message);
      if (document.querySelector('[data-bsp-page-panier]')) { location.reload(); return; }
      BSP.majPanier(data.sections);
    } catch (e) {
      zone && zone.classList.remove('is-chargement');
      if (erreur) { erreur.textContent = (BSP.textes && BSP.textes.erreurCode) || e.message; erreur.hidden = false; }
    }
  }
  function codesDe(zone) { return (zone.dataset.codes || '').split(',').map((c) => c.trim()).filter(Boolean); }
  function initCodePromo() {
    const appliquer = (zone) => {
      const champ = zone.querySelector('[data-bsp-code-champ]');
      const code = (champ.value || '').trim();
      if (!code) { champ.focus(); return; }
      const codes = codesDe(zone);
      if (!codes.some((c) => c.toLowerCase() === code.toLowerCase())) codes.push(code);
      appliquerCodes(codes, zone);
    };
    document.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-bsp-code-appliquer]');
      if (btn) { e.preventDefault(); appliquer(btn.closest('[data-bsp-code]')); return; }
      const retirer = e.target.closest('[data-bsp-code-retirer]');
      if (retirer) {
        e.preventDefault();
        const zone = retirer.closest('[data-bsp-code]');
        appliquerCodes(codesDe(zone).filter((c) => c !== retirer.dataset.bspCodeRetirer), zone);
      }
    });
    // Ouverture du repli « Vous avez un code promo ? » : le curseur va dans le champ
    document.addEventListener('toggle', (e) => {
      if (!e.target.matches?.('[data-bsp-code-repli]') || !e.target.open) return;
      e.target.querySelector('[data-bsp-code-champ]')?.focus();
    }, true);
    // Entrée dans le champ : appliquer le code, sans envoyer le formulaire de la page panier
    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Enter' || !e.target.matches('[data-bsp-code-champ]')) return;
      e.preventDefault();
      appliquer(e.target.closest('[data-bsp-code]'));
    });
  }

  function initPanier() {
    document.addEventListener('click', (e) => {
      const ouvrir = e.target.closest('[data-bsp-ouvrir-panier]');
      if (ouvrir && BSP.panierType !== 'page' && tiroirPanier()) {
        e.preventDefault();
        ouvrirTiroir(tiroirPanier(), ouvrir);
        return;
      }
      if (e.target.closest('[data-bsp-fermer-panier]')) { e.preventDefault(); fermerTiroir(tiroirPanier()); return; }

      const suppr = e.target.closest('[data-bsp-supprimer]');
      if (suppr) {
        e.preventDefault();
        const cles = suppr.dataset.bspSupprimer.split(',').filter(Boolean);
        if (cles.length > 1) retirerLignes(cles, suppr.closest('[data-bsp-ligne]'));
        else changerLigne(cles[0], 0, suppr.closest('[data-bsp-ligne]'));
        return;
      }

      const qte = e.target.closest('[data-bsp-qte]');
      if (qte) {
        const ligne = qte.closest('[data-bsp-ligne]');
        const input = ligne.querySelector('[data-bsp-qte-input]');
        const n = Math.max(0, parseInt(input.value, 10) + (qte.dataset.bspQte === 'plus' ? 1 : -1));
        input.value = n;
        clearTimeout(ligne._t);
        ligne._t = setTimeout(() => changerLigne(ligne.dataset.bspLigne, n, ligne), 350);
        return;
      }

      const plus = e.target.closest('[data-bsp-voir-noms]');
      if (plus) {
        const zone = plus.closest('[data-bsp-noms]');
        zone.querySelectorAll('[data-cache]').forEach((n) => { n.hidden = false; });
        plus.remove();
      }
    });

    document.addEventListener('change', (e) => {
      const input = e.target.closest('[data-bsp-qte-input]');
      if (!input) return;
      const ligne = input.closest('[data-bsp-ligne]');
      changerLigne(ligne.dataset.bspLigne, Math.max(0, parseInt(input.value, 10) || 0), ligne);
    });

    // Retour arrière (bfcache) : le panier a pu changer dans un autre onglet.
    window.addEventListener('pageshow', (e) => {
      if (!e.persisted) return;
      fetch(`${location.pathname}?sections=bsp-panier-tiroir`).then((r) => r.json()).then((s) => BSP.majPanier(s)).catch(() => {});
    });
  }

  /* ─── Menu mobile ────────────────────────────────────────────────── */
  function initMenu() {
    document.addEventListener('click', (e) => {
      const ouvrir = e.target.closest('[data-bsp-ouvrir-menu]');
      if (ouvrir) { e.preventDefault(); ouvrirTiroir(document.querySelector('[data-bsp-menu]'), ouvrir); ouvrir.setAttribute('aria-expanded', 'true'); return; }
      if (e.target.closest('[data-bsp-fermer-menu]')) {
        e.preventDefault();
        fermerTiroir(document.querySelector('[data-bsp-menu]'));
        document.querySelectorAll('[data-bsp-ouvrir-menu]').forEach((b) => b.setAttribute('aria-expanded', 'false'));
      }
    });
  }

  /* ─── Langues ────────────────────────────────────────────────────── */
  function initLangues() {
    document.addEventListener('click', (e) => {
      const b = e.target.closest('[data-bsp-langue]');
      if (!b) return;
      const form = document.querySelector('[data-bsp-form-langue]');
      if (!form) return;
      form.querySelector('input[name="locale_code"]').value = b.dataset.bspLangue;
      form.submit();
    });
    document.addEventListener('click', (e) => {
      document.querySelectorAll('.bsp-langues[open]').forEach((d) => { if (!d.contains(e.target)) d.removeAttribute('open'); });
    });
  }

  /* ─── Apparitions au défilement ──────────────────────────────────── */
  function initReveal(racine = document) {
    const els = racine.querySelectorAll('[data-bsp-reveal]:not(.is-visible)');
    if (!els.length) return;
    if (reduitMouvement || !('IntersectionObserver' in window) || document.body.classList.contains('shopify-design-mode')) {
      els.forEach((el) => el.classList.add('is-visible'));
      return;
    }
    const io = new IntersectionObserver((entrees) => {
      entrees.forEach((e) => { if (e.isIntersecting) { e.target.classList.add('is-visible'); io.unobserve(e.target); } });
    }, { threshold: 0.12, rootMargin: '0px 0px -40px 0px' });
    els.forEach((el) => io.observe(el));
  }
  BSP.initReveal = initReveal;

  /* ─── Badge « note Google » de l'en-tête : note et nombre à jour depuis le flux ─── */
  function initNoteGoogle() {
    const badges = document.querySelectorAll('[data-bsp-note-google][data-flux]:not([data-flux=""])');
    if (!badges.length) return;
    fetch(badges[0].dataset.flux).then((r) => (r.ok ? r.json() : null)).then((data) => {
      if (!data || !data.note) return;
      const langue = document.documentElement.lang || 'fr';
      const note = new Intl.NumberFormat(langue, { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(data.note);
      const noteAffichee = Number.isInteger(data.note) ? String(data.note) : note; // 5 → « 5/5 »
      const nombre = data.total ? new Intl.NumberFormat(langue).format(data.total) : null;
      badges.forEach((b) => {
        b.querySelector('[data-note]').textContent = noteAffichee;
        if (!nombre) return;
        b.querySelector('[data-nombre]').textContent = b.dataset.nombreModele.replace('__N__', nombre);
        b.querySelector('[data-compte]').textContent = `(${nombre})`;
        b.setAttribute('aria-label', b.dataset.aria.replace('__NOTE__', note).replace('__N__', nombre));
      });
    }).catch(() => { /* flux indisponible : on garde les valeurs des réglages */ });
  }

  /* ─── Démarrage ──────────────────────────────────────────────────── */
  function init() {
    if (window.Shopify && window.Shopify.designMode) document.body.classList.add('shopify-design-mode');
    initEntete();
    initPanier();
    initCodePromo();
    initNoteGoogle();
    initMenu();
    initLangues();
    initReveal();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();

  document.addEventListener('shopify:section:load', (e) => { initReveal(e.target); if (e.target.querySelector('[data-bsp-entete]')) initEntete(); });
})();
